// ChatContextPicker — seletor de CONTEXTO do composer do Chat.
//
// Uma pill "Contexto" no rodapé + dropdown com três caminhos, exatamente o que o
// Pedro pediu ("context: repo GitHub, ou nenhum, ou local"):
//   • Nenhum            → conversa pura (limpa o contexto)
//   • Pasta local…      → picker nativo do Electron (window.apolo.repo.pickFolder)
//   • Repositório GitHub → lista real de GET /api/github/repos (recentes + busca),
//                          reusa a mesma infra da RepoPickerPill do Code
//
// Controlado: o composer abre/fecha via `open`/`onOpenChange` (é o mesmo painel
// que o `@` no textarea dispara) e recebe a escolha via `onChange(context)`.
// O contexto escolhido flui pro createCodeSession por contextToSessionInput().

import { useEffect, useRef, useState } from 'react';
import { Command as CmdkCommand } from 'cmdk';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, FolderOpen, Github, Loader2, Lock, Plus, Slash, X } from 'lucide-react';
import { apiFetch } from '../../../lib/api.js';
import { getRecentRepos, pushRecentRepo } from '../../../lib/recentRepos.js';
import { contextLabel } from '../../../lib/chatContext.js';

const SPRING = { type: 'spring', stiffness: 320, damping: 28 };

export default function ChatContextPicker({ value, onChange, open, onOpenChange, canPickFolder, onPickFolder }) {
  const [view, setView] = useState('root'); // 'root' | 'github'
  const wrapRef = useRef(null);

  // Fecha em clique fora / Esc.
  useEffect(() => {
    if (!open) return undefined;
    function onDown(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) onOpenChange(false); }
    function onKey(e) { if (e.key === 'Escape') { e.stopPropagation(); onOpenChange(false); } }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open, onOpenChange]);

  // Sempre reabre no menu raiz.
  useEffect(() => { if (open) setView('root'); }, [open]);

  function choose(next) { onChange(next); onOpenChange(false); }
  function pickRepo(repo) {
    const entry = {
      name: repo.name,
      full_name: repo.full_name,
      url: repo.url,
      clone_url: repo.clone_url,
      default_branch: repo.default_branch || 'main',
      private: !!repo.private,
    };
    pushRecentRepo(entry);
    choose({ kind: 'github', repo: entry });
  }
  async function pickFolder() { onOpenChange(false); await onPickFolder?.(); }

  const label = contextLabel(value) || 'Contexto';
  const TriggerIcon = value?.kind === 'github' ? Github : value?.kind === 'folder' ? FolderOpen : ChevronDown;

  return (
    <div ref={wrapRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={value ? `Contexto: ${label}. Trocar.` : 'Adicionar contexto (repo, pasta ou nenhum)'}
        title={value?.kind === 'folder' ? value.path : value?.kind === 'github' ? value.repo.full_name : 'Adicionar contexto'}
        className={
          'inline-flex h-8 max-w-[180px] items-center gap-1.5 rounded-lg px-2 text-xs transition ' +
          (value
            ? 'bg-apolo-accentMuted text-apolo-textPrimary'
            : 'text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary')
        }
      >
        <TriggerIcon size={15} aria-hidden="true" className={value ? 'text-apolo-accent' : ''} />
        {value && <span className="max-w-[110px] truncate">{label}</span>}
        {value && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); choose(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); choose(null); } }}
            aria-label="Limpar contexto"
            className="ml-0.5 shrink-0 rounded px-0.5 text-apolo-textTertiary hover:text-apolo-textPrimary"
          >
            <X size={12} aria-hidden="true" />
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98 }}
            transition={SPRING}
            className="absolute bottom-full left-0 z-40 mb-1 w-72 overflow-hidden rounded-xl border border-apolo-borderSubtle bg-apolo-surface1 shadow-[var(--apolo-glass-card-shadow)]"
          >
            {view === 'root' ? (
              <RootMenu
                value={value}
                canPickFolder={canPickFolder}
                onNone={() => choose(null)}
                onFolder={pickFolder}
                onGithub={() => setView('github')}
              />
            ) : (
              <GithubMenu onPick={pickRepo} onBack={() => setView('root')} />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function RootRow({ icon: Icon, label, desc, onClick, chevron, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'flex w-full items-center gap-2.5 px-3 py-2 text-left transition hover:bg-apolo-surface2 ' +
        (active ? 'bg-apolo-surface2' : '')
      }
    >
      <Icon size={15} aria-hidden="true" className="shrink-0 text-apolo-textTertiary" />
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-[12px] font-medium text-apolo-textPrimary">{label}</span>
        {desc && <span className="truncate text-[10px] text-apolo-textTertiary">{desc}</span>}
      </span>
      {chevron && <ChevronDown size={13} aria-hidden="true" className="ml-auto -rotate-90 text-apolo-textTertiary" />}
    </button>
  );
}

function RootMenu({ value, canPickFolder, onNone, onFolder, onGithub }) {
  return (
    <div className="flex flex-col py-1" role="menu" aria-label="Escolher contexto">
      <div className="px-3 pb-1 pt-1.5 text-[10px] font-medium uppercase tracking-wide text-apolo-textTertiary">
        Contexto da sessão
      </div>
      <RootRow icon={Slash} label="Nenhum" desc="Conversa pura, sem arquivos" onClick={onNone} active={!value} />
      {canPickFolder && (
        <RootRow icon={FolderOpen} label="Pasta local…" desc="Escolher um diretório do Mac" onClick={onFolder} />
      )}
      <RootRow icon={Github} label="Repositório GitHub" desc="Abrir/clonar um repo seu" onClick={onGithub} chevron />
    </div>
  );
}

function GithubMenu({ onPick, onBack }) {
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notConnected, setNotConnected] = useState(false);
  const [creating, setCreating] = useState(false);
  const [recents] = useState(() => getRecentRepos());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null); setNotConnected(false);
      const res = await apiFetch('/api/github/repos', { method: 'GET' }).catch(() => ({ ok: false }));
      if (cancelled) return;
      if (!res.ok) {
        if (res.status === 404 || res.code === 'GITHUB_NOT_CONNECTED') setNotConnected(true);
        else setError(res.error || 'Falha ao carregar repositórios');
      } else {
        setRepos(Array.isArray(res.data?.repos) ? res.data.repos : []);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Fluxo "+ Novo repositório" (mesmo do RepoPickerPill do Code): cria na conta
  // GitHub e já seleciona como contexto.
  if (creating) {
    return <NewRepoForm onCreated={onPick} onCancel={() => setCreating(false)} />;
  }

  const recentSet = new Set(recents.map((r) => r.full_name));
  const rest = repos.filter((r) => !recentSet.has(r.full_name));
  const itemCls =
    'flex cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1.5 text-[12px] text-apolo-textSecondary data-[selected=true]:bg-apolo-surface2 data-[selected=true]:text-apolo-textPrimary';
  const headingCls =
    '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-1.5 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-apolo-textTertiary';

  const renderItem = (repo) => (
    <CmdkCommand.Item
      key={repo.id || repo.full_name}
      value={repo.full_name || repo.name}
      keywords={[repo.name].filter(Boolean)}
      onSelect={() => onPick(repo)}
      className={itemCls}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        {repo.private && <Lock size={10} className="shrink-0 text-apolo-textTertiary" aria-hidden="true" />}
        <span className="truncate">{repo.full_name || repo.name}</span>
      </span>
      {repo.language && <span className="shrink-0 text-[10px] text-apolo-textTertiary">{repo.language}</span>}
    </CmdkCommand.Item>
  );

  return (
    <CmdkCommand label="Repositórios do GitHub" className="flex flex-col">
      <div className="flex items-center gap-2 border-b border-apolo-borderSubtle px-3 py-2">
        <button
          type="button"
          onClick={onBack}
          aria-label="Voltar"
          className="shrink-0 rounded text-apolo-textTertiary hover:text-apolo-textPrimary"
        >
          <ChevronDown size={14} aria-hidden="true" className="rotate-90" />
        </button>
        <Github size={13} aria-hidden="true" className="text-apolo-textTertiary" />
        <CmdkCommand.Input
          autoFocus
          placeholder="Buscar repositório…"
          className="w-full bg-transparent text-[12px] text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none"
        />
      </div>
      {!loading && !notConnected && !error && (
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="flex w-full items-center gap-1.5 border-b border-apolo-borderSubtle px-3 py-2 text-left text-[12px] font-medium text-apolo-accent hover:bg-apolo-surface2"
        >
          <Plus size={13} aria-hidden="true" />
          Novo repositório
        </button>
      )}
      <CmdkCommand.List className="max-h-64 overflow-y-auto p-1">
        {loading && (
          <div className="flex items-center justify-center gap-2 py-6 text-[11px] text-apolo-textTertiary">
            <Loader2 size={12} className="animate-spin" aria-hidden="true" />
            Buscando repositórios…
          </div>
        )}
        {!loading && notConnected && (
          <div className="flex flex-col items-start gap-2 px-2 py-3 text-[11px] text-apolo-textSecondary">
            Conecte o GitHub para listar seus repositórios.
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('apolo:openConnections'))}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 text-[11px] font-medium text-apolo-textPrimary hover:border-apolo-highlightRing"
            >
              <Github size={11} aria-hidden="true" />
              Conectar GitHub
            </button>
          </div>
        )}
        {!loading && error && <div className="px-2 py-3 text-[11px] text-apolo-error" role="alert">{error}</div>}
        {!loading && !notConnected && !error && (
          <>
            <CmdkCommand.Empty className="px-2 py-3 text-[11px] text-apolo-textTertiary">
              Nenhum repositório encontrado.
            </CmdkCommand.Empty>
            {recents.length > 0 && (
              <CmdkCommand.Group heading="Recentes" className={headingCls}>
                {recents.map(renderItem)}
              </CmdkCommand.Group>
            )}
            <CmdkCommand.Group heading={recents.length > 0 ? 'Todos' : undefined} className={headingCls}>
              {rest.map(renderItem)}
            </CmdkCommand.Group>
          </>
        )}
      </CmdkCommand.List>
    </CmdkCommand>
  );
}

// Cria um repo novo (privado, com README) na conta do GitHub e já o entrega como
// contexto. Espelha o form da RepoPickerPill do Code. POST /api/github/repos.
function NewRepoForm({ onCreated, onCancel }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function create() {
    const n = name.trim();
    if (!n || busy) return;
    setBusy(true); setError(null);
    const res = await apiFetch('/api/github/repos', {
      method: 'POST',
      body: { name: n, private: true },
    }).catch(() => ({ ok: false }));
    setBusy(false);
    if (!res.ok) {
      setError(res.status === 404 || res.code === 'GITHUB_NOT_CONNECTED'
        ? 'Conecte o GitHub primeiro.'
        : res.error || 'Falha ao criar repositório');
      return;
    }
    if (res.data?.repo) onCreated(res.data.repo);
  }

  return (
    <div className="flex flex-col gap-2 p-3">
      <div className="flex items-center gap-2 text-[11px] font-medium text-apolo-textSecondary">
        <Plus size={13} aria-hidden="true" className="text-apolo-textTertiary" />
        Novo repositório
      </div>
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); create(); }
          if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCancel(); }
        }}
        placeholder="nome-do-repositorio"
        aria-label="Nome do novo repositório"
        className="w-full rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1.5 text-[12px] text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none"
      />
      <p className="text-[10px] text-apolo-textTertiary">Privado, inicializado com README na sua conta GitHub.</p>
      {error && <p className="text-[11px] text-apolo-error" role="alert">{error}</p>}
      <div className="flex items-center justify-end gap-1.5">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex h-7 items-center rounded-md px-2 text-[11px] text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={create}
          disabled={!name.trim() || busy}
          className="inline-flex h-7 items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 text-[11px] font-medium text-apolo-textPrimary hover:border-apolo-accent disabled:cursor-not-allowed disabled:opacity-40"
        >
          {busy ? <Loader2 size={11} className="animate-spin" aria-hidden="true" /> : <Plus size={11} aria-hidden="true" />}
          Criar
        </button>
      </div>
    </div>
  );
}
